import { type ComboboxOptionType, DropdownSelect, FormField } from '@zillow/constellation-10';
import { useContext, useMemo } from 'react';
import useMultiFamilyLeadsRouterHelper from '../../hooks/useMultiFamilyLeadsRouterHelper';
import { MultiFamilyLeadsPageContext } from '../../contexts/MultiFamilyLeadsContextProvider';
import { getMonthsForTimeFrameDropdown } from '../../utils/timeFrameDropdownUtils';
import {
  emitTimePeriodSelectEvent,
  TIME_PERIOD_SELECT_EVENT_TYPE_ID,
} from '../../constants/clickstreamEvents/multiFamilyLeadsPage';
import { useAuth } from '../../AuthProvider';

export const DEFAULT_DAYS_BACK = 7;

const MONTH_OPTIONS = getMonthsForTimeFrameDropdown();

export const TIME_FRAME_DROPDOWN_OPTIONS = [
  {
    label: 'Today',
    value: '0',
  },
  {
    label: 'Yesterday',
    value: '1',
  },
  {
    label: `Last ${DEFAULT_DAYS_BACK} days`,
    value: String(DEFAULT_DAYS_BACK),
  },
  {
    label: 'Last 14 days',
    value: '14',
  },
  {
    label: 'Last 30 days',
    value: '30',
  },
  {
    label: 'Last 90 days',
    value: '90',
  },
  ...MONTH_OPTIONS,
];

export default function TimeFrameDropdown() {
  const { daysBack, startDate } = useContext(MultiFamilyLeadsPageContext);
  const { updatePathWithTimeFrameDropdownValue } = useMultiFamilyLeadsRouterHelper();
  const { user } = useAuth();

  const defaultValue = useMemo(
    () =>
      startDate
        ? TIME_FRAME_DROPDOWN_OPTIONS.find(({ value }) => value === startDate)?.value
        : TIME_FRAME_DROPDOWN_OPTIONS.find(({ value }) => value === daysBack)?.value,
    [daysBack, startDate],
  );

  const handleSelectDate = (option: ComboboxOptionType) => {
    emitTimePeriodSelectEvent(user);

    if (typeof option === 'object' && option !== null && 'value' in option) {
      const { value } = option;

      // Convert numeric strings ('0', '1', '7', etc.) back to numbers for daysBack logic
      // Date strings (like '20230301') will remain strings for startDate logic
      const timeFrameValue = /^\d{1,2}$/.test(value) ? Number(value) : value;

      updatePathWithTimeFrameDropdownValue({
        timeFrameValue,
        shouldReplaceHistory: false,
      });
    }
  };

  return (
    <FormField
      css={{
        minWidth: '309px',
      }}
      control={
        <DropdownSelect
          data-eventid={TIME_PERIOD_SELECT_EVENT_TYPE_ID}
          defaultValue={defaultValue}
          onOptionSelect={handleSelectDate}
          options={TIME_FRAME_DROPDOWN_OPTIONS}
          placeholder="Select time frame..."
          showLabelForValue
          shouldAwaitInteractionResponse
          size="lg"
          css={{
            backgroundColor: 'bg.action.neutral.default',
          }}
        />
      }
    />
  );
}
